import React, { useState } from "react";
import {
  MessageCircle,
  Loader2,
  Download,
  AlertTriangle,
  Users,
  Building2,
  Shield,
} from "lucide-react";
import {
  Notification,
  NotificationType,
} from "../../services/NotificationService";
import { userService } from "../../services/UserService";
import { useToastContext } from "../../context/ToastContext";
import { formatFriendlyDate } from "../../utils/DateUtils";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

interface NotificationItemProps {
  notification: Notification;
  refreshNotifications: () => void;
  onMarkAsRead?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  refreshNotifications,
  onMarkAsRead,
}) => {
  const [isLoading, setIsLoading] = useState<"accept" | "decline" | null>(null);
  const isUnread = !notification.read;
  const { showToast } = useToastContext();
  const { dataRefresh } = useAuth();
  const navigate = useNavigate();

  const getNotificationIcon = () => {
    switch (notification.type) {
      case NotificationType.Invitation:
        return <MessageCircle className="w-4 h-4" />;
      case NotificationType.DataExportReady:
        return <Download className="w-4 h-4" />;
      case NotificationType.SecurityAlert:
        return <Shield className="w-4 h-4" />;
      case NotificationType.MemberAdded:
      case NotificationType.MemberRemoved:
      case NotificationType.RoleChanged:
        return <Users className="w-4 h-4" />;
      case NotificationType.OrganizationDeleted:
      case NotificationType.OwnershipTransferred:
        return <Building2 className="w-4 h-4" />;
      case NotificationType.SyncError:
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const handleClick = () => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead();
    }
    handleNotificationAction();
  };

  const handleNotificationAction = () => {
    switch (notification.type) {
      case NotificationType.Invitation:
        if (notification.metadata?.inviteToken) {
          navigate(
            `/accept-invitation?token=${notification.metadata.inviteToken}`
          );
        }
        break;
      case NotificationType.MemberAdded:
      case NotificationType.MemberRemoved:
      case NotificationType.RoleChanged:
        if (notification.metadata?.organizationId) {
          navigate(`/settings?tab=organizations`);
        }
        break;
      case NotificationType.OrganizationDeleted:
        navigate("/settings?tab=organizations");
        break;
      case NotificationType.DataExportReady:
        if (notification.metadata?.downloadUrl) {
          window.open(notification.metadata.downloadUrl, "_blank");
        }
        break;
      case NotificationType.SecurityAlert:
        navigate("/settings?tab=profile");
        break;
      case NotificationType.SyncError:
        if (notification.metadata?.organizationId) {
          navigate("/field-mapping");
        }
        break;
      default:
        break;
    }
  };

  const handleAction = async (action: "accept" | "decline") => {
    setIsLoading(action);

    if (notification.type === NotificationType.Invitation) {
      const token = notification.metadata?.inviteToken ?? null;

      if (!token) {
        showToast("error", "Invalid invitation token. Please try again.");
        setIsLoading(null);
        return;
      }

      try {
        let response;
        if (action === "accept") {
          console.log(`Accepting invitation with token: ${token}`);
          response = await userService.acceptInvitation(token);
          notification.metadata.invitationStatus = "accepted";
        } else {
          console.log(`Declining invitation with token: ${token}`);
          response = await userService.declineInvitation(token);
          notification.metadata.invitationStatus = "declined";
        }

        console.log(`Invitation ${action}ed:`, response.message);
        showToast(
          "success",
          response.message || `Invitation ${action}ed successfully!`
        );

        notification.read = true;
        if (onMarkAsRead) {
          onMarkAsRead();
        }

        await dataRefresh();
      } catch (error: any) {
        console.error(`Error ${action}ing invitation:`, error);
        showToast("error", error.message || `Failed to ${action} invitation.`);
      }
    }

    setTimeout(() => {
      setIsLoading(null);
      refreshNotifications();
    }, 500);
  };

  return (
    <div
      className={`
        flex flex-col gap-2 px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer
        ${
          isUnread
            ? "bg-primary/10 dark:bg-background-panelDark/90 shadow-lg"
            : "bg-background-default dark:bg-background-panelDark/30 text-text-primary dark:text-gray-300"
        }
        hover:bg-primary/20 dark:hover:bg-background-panelDark/80
      `}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center ${
            notification.priority === "high"
              ? "bg-red-500/10 dark:bg-red-500/20 text-red-500"
              : "bg-secondary/10 dark:bg-[#1e293b] text-secondary dark:text-[#CBD5E1]"
          }`}
        >
          {getNotificationIcon()}
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{notification.title}</h4>
            <div className="flex items-center gap-2">
              {notification.type === NotificationType.SyncError &&
                notification.metadata?.errorCount &&
                notification.metadata.errorCount > 1 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-orange-500 text-white rounded">
                    {notification.metadata.errorCount} errors
                  </span>
                )}
              {notification.priority === "high" && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-red-500 text-white rounded">
                  High
                </span>
              )}
              {isUnread && (
                <span className="w-2 h-2 bg-secondary rounded-full"></span>
              )}
            </div>
          </div>
          <p className="text-xs mt-1 text-text-secondary dark:text-gray-400">
            {notification.message}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            {formatFriendlyDate(notification.createdAt)}
          </p>
        </div>
      </div>

      {notification.type === NotificationType.Invitation &&
        notification.metadata?.invitationStatus !== "accepted" &&
        notification.metadata?.invitationStatus !== "declined" && (
          <div
            className="flex gap-3 mt-1 ml-11"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="px-3 py-1 text-xs font-medium bg-transparent text-green-600 dark:text-green-400 hover:underline transition flex items-center gap-2"
              onClick={() => handleAction("accept")}
              disabled={isLoading === "accept"}
            >
              {isLoading === "accept" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Accept"
              )}
            </button>

            <button
              className="px-3 py-1 text-xs font-medium bg-transparent text-red-600 dark:text-red-400 hover:underline transition flex items-center gap-2"
              onClick={() => handleAction("decline")}
              disabled={isLoading === "decline"}
            >
              {isLoading === "decline" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Decline"
              )}
            </button>
          </div>
        )}

      {notification.type === NotificationType.DataExportReady &&
        notification.metadata?.downloadUrl && (
          <div className="mt-1 ml-11" onClick={(e) => e.stopPropagation()}>
            <button
              className="px-3 py-1 text-xs font-medium bg-secondary/10 dark:bg-secondary/20 text-secondary hover:bg-secondary/20 dark:hover:bg-secondary/30 rounded transition flex items-center gap-2"
              onClick={() => {
                window.open(notification.metadata.downloadUrl, "_blank");
              }}
            >
              <Download className="w-3 h-3" />
              Download Export
            </button>
          </div>
        )}
    </div>
  );
};

export default NotificationItem;
